import type { Operation } from '@trpc/client';

export const SSE_BASE_DELAY_MS = 1_000;
export const SSE_MAX_DELAY_MS = 30_000;
export const SSE_MAX_CONSECUTIVE_FAILURES = 10;
export const SSE_HEALTHY_CONNECTION_MS = 60_000;

const STALE_OP_MS = 5 * 60_000;

type FailureState = {
  failures: number;
  at: number;
  needsFreshToken: boolean;
  refreshedThisStreak: boolean;
};

export const isUnauthorized = (error: unknown): boolean => {
  const cause = (error as { cause?: unknown } | undefined)?.cause;
  return (cause as { status?: unknown } | undefined)?.status === 401;
};

export const backoffDelayMs = (failures: number, random: () => number = Math.random): number => {
  const ceiling = Math.min(SSE_BASE_DELAY_MS * 2 ** (failures - 1), SSE_MAX_DELAY_MS);
  const half = ceiling / 2;
  return Math.round(half + random() * half);
};

export const createSseRetryPolicy = ({
  now = Date.now,
  random = Math.random,
}: {
  now?: () => number;
  random?: () => number;
} = {}) => {
  const failuresByOp = new Map<number, FailureState>();
  let pendingDelayMs = SSE_BASE_DELAY_MS;

  const prune = (at: number) => {
    for (const [id, state] of failuresByOp) {
      if (at - state.at > STALE_OP_MS) {
        failuresByOp.delete(id);
      }
    }
  };

  return {
    retry: ({ op, error }: { op: Operation; error: unknown }): boolean => {
      const at = now();
      const previous = failuresByOp.get(op.id);
      const continuesStreak =
        previous !== undefined && at - previous.at < SSE_HEALTHY_CONNECTION_MS;
      const failures = continuesStreak ? previous.failures + 1 : 1;
      const refreshedThisStreak = continuesStreak && previous.refreshedThisStreak;

      // One forced refresh per streak: if a fresh token is still rejected the problem is not
      // staleness, and re-minting on every retry would just hammer Auth0.
      const needsFreshToken = isUnauthorized(error) && !refreshedThisStreak;

      failuresByOp.set(op.id, {
        failures,
        at,
        needsFreshToken,
        refreshedThisStreak: refreshedThisStreak || needsFreshToken,
      });
      prune(at);

      pendingDelayMs = backoffDelayMs(failures, random);
      return failures <= SSE_MAX_CONSECUTIVE_FAILURES;
    },
    // retryLink calls retryDelayMs() synchronously right after retry() for the same operation,
    // so the delay computed there is still the one that belongs to this attempt.
    retryDelayMs: (): number => pendingDelayMs,
    consumeFreshTokenRequest: (opId: number): boolean => {
      const state = failuresByOp.get(opId);
      if (!state?.needsFreshToken) {
        return false;
      }
      state.needsFreshToken = false;
      return true;
    },
  };
};
